import { LogLevel } from "@azure/msal-browser";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonAddAltSharpIcon from "@mui/icons-material/PersonAddAltSharp";
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import TextField from '@mui/material/TextField';
import { useContext, useEffect, useState } from "react";
import { symbolicateCrashesOfApp } from "src/services/crashService";
import {
  getDefectTracker,
  getIssueTypes,
  getProjectList,
  getTrackedApplication,
  trackApplication,
  untrackApplication,
} from "src/services/defectTrackerService";
import { Log } from "src/utilities/helper";

import GrantAccessDialog from "components/Application/GrantAccessDialog";
import { applicationDetails } from "dto/appDto";
import { selectAuth } from "slices/authSlice";
import { useAppSelector } from "store/hooks";
import dialogContext, { DialogType } from "utilities/dialogContext";
import { getTrackedWarningThreshold, trackWarningThreshold, untrackWarningThreshold } from "src/services/appsService";
import { isNumber } from "lodash";
import GrantNotificationDialog from "./GrantNotificationDialog";

type AppDetailsPanelProps = {
  app: applicationDetails;
  showGrantAccessButton: boolean;
  updateNotificationUserList?: () => void;
};
interface projectDropdownDto {
  key: string;
  name: string;
}
interface issueTypeDto {
  id: string;
  name: string;
  iconURL: string;
}
interface applicationTrackerDto {
  applicationId: number;
  projectKey: string;
  trackerType: number;
  trackingMode: number;
  issueTypeId: string;
}

interface applicationWarningThresholdDto {
  applicationId: number;
  unit: number;
  threshold: number;
  minimumLast30DaysAverage: number;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};
export default function AppDetailsPanel({
  app,
  showGrantAccessButton = false,
  updateNotificationUserList
}: AppDetailsPanelProps) {
  const auth = useAppSelector(selectAuth);
  const [open, setOpen] = useState(false);
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
  var {
    setDialogType,
    setShowDialog,
    setTitle,
    setMessage,
    setOnClickOk,
    setOnClickCancel,
  } = useContext(dialogContext);

  function closeGrantAccessDialog() {
    setOpen(false);
  }

  function closeGrantNotificationDialog() {
    setOpenNotificationDialog(false);
    updateNotificationUserList?.();
  }

  const symbolicateCrashes = () => {
    setTitle("Symbolication");
    setMessage(
      "All the unique and unsymbolicated crashes will be symbolicated. Are you sure?"
    );
    setDialogType(DialogType.CONFIRMATION);
    setOnClickOk(() => () => {
      symbolicateCrashesOfApp(auth.authToken, app.id).then((response) => {
        if (response[0]) {
          var result = response[0];
          if (result.success) {
            setMessage(
              "Symbolication has been started. This may take some time. Please come back later."
            );
          } else {
            Log(result.message);
            setTitle("Error");
            setMessage("Something went wrong. Please try again later");
          }

          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
          setShowDialog(true);
        } else {
          var error = response[1];
          Log(error, LogLevel.Error);
        }
      });
    });
    setOnClickCancel(() => () => {
      setShowDialog(false);
    });
    setShowDialog(true);
  };

  const [showTrackingOption, setShowTrackingOption] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");
  const [issueType, setIssueType] = useState("");
  const [projectList, setProjectList] = useState<projectDropdownDto[]>([]);
  const [issueTypeList, setIssueTypeList] = useState<issueTypeDto[]>([]);
  const [alreadyTracked, setAlreadyTracked] = useState(false);

  const [newProject, setNewProject] = useState("");
  const [newIssueType, setNewIssueType] = useState("");

  // Warning threshold
  const [selectedUnit, setSelectedUnit] = useState(0);
  const [warningThresholdValue, setWarningThresholdValue] = useState<number>(0);
  const [minimumAverageValue, setMinimumAverageValue] = useState<number>(50);
  const [alreadyTrackedWarningThreshold, setAlreadyTrackedWarningThreshold] = useState(false);

  const [newWarningThresholdValue, setNewWarningThresholdValue] = useState<number|''>('');
  const [newMinimumAverageValue, setNewMinimumAverageValue] = useState<number|''>(50);
  const [newWarningThresholdUnit, setNewWarningThresholdUnit] = useState<number>(0);

  const loadDefectTracker = () => {
    getDefectTracker(auth.authToken, 1).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setShowTrackingOption(true);
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  const loadTrackedApplication = () => {
    getTrackedApplication(auth.authToken, 1, app.id).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setAlreadyTracked(true);
          setSelectedProject(result.data["projectKey"]);
          setIssueType(result.data["issueTypeId"]);

          handleProjectChange(
            result.data["projectKey"],
            result.data["issueTypeId"]
          );
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  const handleProjectChange = (value: string, issue: string) => {
    setNewProject(value);
    setNewIssueType(issue);
    getIssueTypes(auth.authToken, 1, Number(value)).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setIssueTypeList(result.data);
        } else {
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  const handleIssuTypeChanges = (event: SelectChangeEvent) => {
    setNewIssueType(event.target.value);
  };

  const handleButtonClick = () => {
    if (alreadyTracked) {
      if (issueType === newIssueType && selectedProject === newProject) {
        //remove
        unbindApplication();
      } else {
        //update
        bindApplication();
      }
    } else {
      //add
      bindApplication();
    }
  };

  const bindApplication = () => {
    var data: applicationTrackerDto = {
      applicationId: app.id,
      projectKey: newProject,
      trackerType: 1,
      trackingMode: 2,
      issueTypeId: newIssueType,
    };

    trackApplication(auth.authToken, data).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setTitle("Success");
          setMessage(result.message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
          setShowDialog(true);

          setAlreadyTracked(true);
          setSelectedProject(newProject);
          setIssueType(newIssueType);
        } else {
          setTitle("Error");
          setMessage(result.message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
          setShowDialog(true);
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  const unbindApplication = () => {
    var data = {
      applicationId: app.id,
      trackerType: 1,
    };

    untrackApplication(auth.authToken, data).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setSelectedProject("");
          setIssueType("");
          setNewProject("");
          setNewIssueType("");
          setAlreadyTracked(false);
        } else {
          setTitle("Error");
          setMessage(result.message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
          setShowDialog(true);
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  const loadProjectList = () => {
    getProjectList(auth.authToken, 1).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setProjectList(result.data);
        } else {
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };


  // Handle warning threshold
  const handleWarningThresholdButtonClick = () => {
    if (alreadyTrackedWarningThreshold) {
      if (selectedUnit === newWarningThresholdUnit && warningThresholdValue === newWarningThresholdValue && minimumAverageValue === newMinimumAverageValue) {
        //remove
        unbindWarningThreshold();
      } else {
        //update
        bindWarningThreshold();
      }
    } else {
      //add
      bindWarningThreshold();
    }
  };

  const handleWarningThresholdUnitChanges = (value: string) => {
    setNewWarningThresholdUnit(Number(value));
  };

  const handleWarningThresholdValueChanges = (value: string) => {
    let newInput: number | "" = Number(value) === 0 ? "" : Number(value);
    setNewWarningThresholdValue(newInput);
  };

  const handleMinimumAverageValueChanges = (value: string) => {
    let newInput: number | "" = Number(value) === 0 ? "" : Number(value);
    setNewMinimumAverageValue(newInput);
  };

  const loadWarningThresholdApplication = () => {
    getTrackedWarningThreshold(auth.authToken, app.id).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setAlreadyTrackedWarningThreshold(true);
          setSelectedUnit(result.data["unit"]);
          setWarningThresholdValue(result.data["threshold"]);
          setMinimumAverageValue(result.data["minimumLast30DaysAverage"]);

          handleWarningThresholdUnitChanges(result.data["unit"]);
          handleWarningThresholdValueChanges(result.data["threshold"]);
          handleMinimumAverageValueChanges(result.data["minimumLast30DaysAverage"]);
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  const bindWarningThreshold = () => {
    var data: applicationWarningThresholdDto = {
      applicationId: app.id,
      unit: newWarningThresholdUnit,
      threshold: Number(newWarningThresholdValue),
      minimumLast30DaysAverage: Number(newMinimumAverageValue)
    };

    if(data.threshold <= 0) {
      setTitle("Error");
      setMessage("Please set the valid threshold.");
      setDialogType(DialogType.MESSAGE);
      setOnClickOk(() => () => setShowDialog(false));
      setShowDialog(true);
      return;
    }

    if(data.unit === 1 && data.minimumLast30DaysAverage <= 0) {
      setTitle("Error");
      setMessage("Please set the valid minimum average.");
      setDialogType(DialogType.MESSAGE);
      setOnClickOk(() => () => setShowDialog(false));
      setShowDialog(true);
      return;
    }

    trackWarningThreshold(auth.authToken, data).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setTitle("Success");
          setMessage(result.message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
          setShowDialog(true);

          setAlreadyTrackedWarningThreshold(true);
          setSelectedUnit(newWarningThresholdUnit);
          setWarningThresholdValue(Number(newWarningThresholdValue));
          setMinimumAverageValue(Number(newMinimumAverageValue));
        } else {
          setTitle("Error");
          setMessage(result.message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
          setShowDialog(true);
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  const unbindWarningThreshold = () => {
    untrackWarningThreshold(auth.authToken, app.id).then((response) => {
      if (response[0]) {
        var result = response[0];
        if (result.success) {
          setWarningThresholdValue(0);
          setMinimumAverageValue(50);
          setNewWarningThresholdUnit(0);
          setNewWarningThresholdValue(0);
          setNewMinimumAverageValue(50);
          setAlreadyTrackedWarningThreshold(false);
        } else {
          setTitle("Error");
          setMessage(result.message);
          setDialogType(DialogType.MESSAGE);
          setOnClickOk(() => () => setShowDialog(false));
          setShowDialog(true);
        }
      } else {
        var error = response[1];
        Log(error, LogLevel.Error);
      }
    });
  };

  useEffect(() => {
    loadDefectTracker();
    loadWarningThresholdApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  useEffect(() => {
    if (showTrackingOption) {
      loadTrackedApplication();
      loadProjectList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, showTrackingOption]);

  return (
    <Card sx={{ mb: 2 }}>
      <GrantAccessDialog
        app={app}
        open={open}
        handleClose={closeGrantAccessDialog}
      />

      <GrantNotificationDialog
        app={app}
        open={openNotificationDialog}
        handleClose={closeGrantNotificationDialog}
      />

      <Grid container justifyContent="space-between" alignItems="center" p={3}>
        <Grid item>
          <Typography variant="h3" component="h3" gutterBottom>
            {app.applicationName}
          </Typography>
        </Grid>
        <Grid item>
          <Grid container justifyContent="space-between" alignItems="center">
            {showGrantAccessButton && auth.isAdmin ? (
              // Grant Notification
              <Grid item pr={0.5}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<PersonAddAltSharpIcon fontSize="small" />}
                  onClick={() => {
                    setOpenNotificationDialog(true);
                  }}
                >
                  Grant Notification to User
                </Button>
              </Grid>
            ) : null}

            {showGrantAccessButton && auth.isAdmin ? (
              // Grant Access
              <Grid item pr={0.5}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<PersonAddAltSharpIcon fontSize="small" />}
                  onClick={() => {
                    setOpen(true);
                  }}
                >
                  Grant Access to User
                </Button>
              </Grid>
            ) : null}

            <Grid item pl={0.5}>
              <Button
                variant="contained"
                color="info"
                startIcon={<AutoAwesomeIcon fontSize="small" />}
                onClick={() => {
                  symbolicateCrashes();
                }}
              >
                Symbolicate Crashes
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <Box p={4} sx={{ display: "flex" }}>
        <Grid container>
          <Grid item md={6}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell style={{ borderBottom: "none" }}>
                    <Typography variant="subtitle1">Application Access Key:</Typography>
                  </TableCell>
                  <TableCell style={{ borderBottom: "none" }}>
                    {app.applicationAccessKey}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell style={{ borderBottom: "none" }}>
                    <Typography variant="subtitle1">
                      Application Versions:
                    </Typography>
                  </TableCell>
                  <TableCell style={{ borderBottom: "none" }}>
                    {app.applicationVersion}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "none" }}>
                    <Typography variant="subtitle1">Description:</Typography>
                  </TableCell>
                  <TableCell style={{ borderBottom: "none" }}>
                    {app.description}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          <Grid item md={6}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell style={{ borderBottom: "none" }}>
                    <Typography variant="subtitle1">Status</Typography>
                  </TableCell>
                  <TableCell style={{ borderBottom: "none" }}>
                    <Typography color={app.isArchived ? "error" : "primary"}>
                      {app.isArchived ? "ARCHIVED" : "ACTIVE"}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "none" }}>
                    <Typography variant="subtitle1">
                      Total Crash Count:
                    </Typography>
                  </TableCell>
                  <TableCell style={{ borderBottom: "none" }}>
                    {app.crashCount}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottom: "none" }}>
                    <Typography variant="subtitle1">
                      Symbolication Threshold (unique user count):
                    </Typography>
                  </TableCell>
                  <TableCell style={{ borderBottom: "none" }}>
                    {app.symbolicationThreshold}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          {showTrackingOption ? (
            <>
              <Grid item sm={12} xs={12} lg={12} md={12}>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid
                container
                item
                display="flex"
                direction="row"
                justifyContent="left"
                p={1.25}
                spacing={2.5}
              >
                <Grid item sm={2} xs={2} lg={2} md={2}>
                  <Box sx={{ pb: 2 }}>
                    <Typography variant="subtitle1">
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        Jira Project
                        <Tooltip title="To enable Jira ticket creation for an application's crash. The crash will be automatically archived when its Jira ticket is marked as Done/Closed/Canceled, depending on the project settings.">
                          <HelpOutlineIcon fontSize="small" />
                        </Tooltip>
                      </span>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item sm={3} xs={3} lg={3} md={3}>
                  <FormControl fullWidth>
                    <InputLabel size="small">Select Project</InputLabel>
                    <Select
                      size="small"
                      value={newProject}
                      label="Select Project"
                      onChange={(event) =>
                        handleProjectChange(event.target.value, "")
                      }
                      MenuProps={MenuProps}
                    >
                      {projectList.map((item) => (
                        <MenuItem key={item.key} value={item.key}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item sm={3} xs={3} lg={3} md={3}>
                  <FormControl fullWidth>
                    <InputLabel size="small">Select Issue Type</InputLabel>
                    <Select
                      size="small"
                      value={newIssueType}
                      label="Select Issue Type"
                      onChange={handleIssuTypeChanges}
                      MenuProps={MenuProps}
                    >
                      {issueTypeList.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item sm={2} xs={2} lg={2} md={2}>
                  <Button
                    variant="contained"
                    color={
                      alreadyTracked
                        ? issueType === newIssueType &&
                          selectedProject === newProject
                          ? "error"
                          : "warning"
                        : "primary"
                    }
                    sx={{ height: "100%" }}
                    onClick={handleButtonClick}
                    disabled={!newIssueType || !newProject}
                  >
                    {alreadyTracked
                      ? issueType === newIssueType &&
                        selectedProject === newProject
                        ? "Remove"
                        : "Update"
                      : "Add"}
                  </Button>
                </Grid>
              </Grid>
            </>
          ) : null}
          {/* Start App Warning Threshold */}
          <>
              <Grid item sm={12} xs={12} lg={12} md={12}>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid
                container
                item
                display="flex"
                direction="row"
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "flex-end",
                }}
                p={1.25}
                spacing={2.5}
              >
                <Grid item sm={2} xs={2} lg={2} md={2}>
                  <Box>
                    <Typography variant="subtitle1">
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        Warning Threshold
                        <Tooltip title="A warning email is triggered at 00:00 UTC+0 when the number of similar crashes exceeds the set threshold, sent to users with application-subscription notifications.">
                        <HelpOutlineIcon fontSize="small" />
                      </Tooltip>
                      </span>
                    </Typography>
                  </Box>
                </Grid>
                <Grid container item sm={3} xs={3} lg={3} md={3}>
                  <Grid item sm={8} xs={8} lg={8} md={8}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="standard"
                      placeholder="Threshold"
                      label="Threshold"
                      type="number"
                      value={newWarningThresholdValue}
                      onChange={(event) =>
                        handleWarningThresholdValueChanges(event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item sm={4} xs={4} lg={4} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id="warning-threshold-label"></InputLabel>
                      <Select
                        size="small"
                        value={newWarningThresholdUnit}
                        variant="standard"
                        id="warning-threshold-value"
                        onChange={(event) =>
                          handleWarningThresholdUnitChanges(event.target.value as string)
                        }
                        MenuProps={MenuProps}
                      >
                        <MenuItem value={0}>Absolute</MenuItem>
                        <MenuItem value={1}>Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                {newWarningThresholdUnit === 1 ? (<Grid container item sm={3} xs={3} lg={3} md={3} direction="row"sx={{
                  justifyContent: "flex-start",
                  alignItems: "flex-end",
                }}>
                  
                  <Grid item sm={8} xs={8} lg={8} md={8}>
                    <TextField
                      size="small"
                      fullWidth
                      variant="standard"
                      placeholder="Minimum average of last 30 days"
                      label={
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            Minimum average of last 30 days
                            <Tooltip title="The average number of crashes over the last 30 days needs to exceed the set threshold in order to trigger the warning email.">
                            <HelpOutlineIcon fontSize="small" />
                          </Tooltip>
                        </span>
                      }
                      type="number"
                      value={newMinimumAverageValue}
                      onChange={(event) =>
                        handleMinimumAverageValueChanges(event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item sm={4} xs={4} lg={4} md={4}>
                    <Typography variant="subtitle1">Crashes</Typography>
                  </Grid>
                </Grid>) : <></>}
                <Grid item sm={2} xs={2} lg={2} md={2}>
                  <Button
                    variant="contained"
                    color={
                      alreadyTrackedWarningThreshold
                        ? warningThresholdValue === newWarningThresholdValue &&
                          selectedUnit === newWarningThresholdUnit
                          ? "error"
                          : "warning"
                        : "primary"
                    }
                    sx={{ height: "100%" }}
                    onClick={handleWarningThresholdButtonClick}
                    disabled={( newWarningThresholdUnit != 0 && !newWarningThresholdUnit) || !newWarningThresholdValue}
                  >
                    {alreadyTrackedWarningThreshold
                        ? warningThresholdValue === newWarningThresholdValue &&
                          selectedUnit === newWarningThresholdUnit && minimumAverageValue === newMinimumAverageValue
                        ? "Remove"
                        : "Update"
                      : "Add"}
                  </Button>
                </Grid>
              </Grid>
            </>
            {/* End App Warning Threshold */}
        </Grid>
      </Box>
    </Card>
  );
}
